import { FC, memo, useRef } from 'react';
import map from 'lodash/map';
import type { Identifier, XYCoord } from 'dnd-core';
import update from 'immutability-helper';
import { useDrag, useDrop } from 'react-dnd';
import Switch from 'antd/lib/switch';

import { COLUMN, DragItem, IColumnProps } from 'components/CustomizeDisplayTable/constants';
import { ColumnStyled } from 'components/CustomizeDisplayTable/styled';
import { ICustomizeTableColumn } from 'types/listsTypes';

const ColumnsItem: FC<IColumnProps> = ({
  id,
  title,
  index,
  isChecked,
  isFixed,
  onColumn,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const handleCheck = (isColumnChecked: boolean) => onColumn((columnsState) =>
    map(columnsState, (column) => id === column.dataIndex ? { ...column, isChecked: isColumnChecked } : column));

  const handleMove = (dragIndex: number, hoverIndex: number) => onColumn((columnsState) => update(
    columnsState,
    { $splice: [[dragIndex, 1], [hoverIndex, 0, columnsState[dragIndex] as ICustomizeTableColumn]] },
  ));

  const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
    accept: COLUMN,
    collect: (monitor) => ({ handlerId: monitor.getHandlerId() }),
    hover: (item: DragItem, monitor) => {
      if (!ref.current) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      handleMove(dragIndex, hoverIndex);

      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: COLUMN,
    item: () => ({ id, index }),
    collect: (monitor: any) => ({ isDragging: monitor.isDragging() }),
  });

  !isFixed && drag(drop(ref));

  return (
    <ColumnStyled
      ref={ref}
      isFixed={!!isFixed}
      data-handler-id={handlerId}
      opacity={isDragging ? 0 : 1}
    >
      {title}
      <Switch checked={isChecked} onChange={handleCheck} />
    </ColumnStyled>
  );
};

export default memo(ColumnsItem);
